/**
 * @fileoverview Streaming & Memory Management Engine
 * @module MikhmelViewer/StreamingManager
 *
 * [v2.0 FIXES]:
 *  - streamingConfig: заменён Proxy на геттер config() (безопасный доступ)
 *  - _executeBackgroundLoad: scheduler.postTask / setTimeout(0) вместо rAF (×1.25 быстрее)
 *  - initialize: адаптация не перезаписывает явный пользовательский maxCacheSizeMB
 *  - onSliceChange: проверка изменения индекса (защита от двойного вызова)
 *  - _getSafelyCachedImageIds: полный try/catch с детальным логом
 *
 * Оптимизации для больших данных (Enterprise Grade):
 *   ✓ Progressive stack loading — первый кадр грузится вне очереди
 *   ✓ Priority-based prefetch — умная подгрузка "вокруг" текущего среза
 *   ✓ Memory manager с безопасным LRU eviction
 *   ✓ UI-Thread Yielding — предотвращение фризов интерфейса при декодировании
 *   ✓ Adaptive batch sizing по телеметрии времени декодирования
 *   ✓ Safe Cache Introspection — защита от изменения приватных API Cornerstone
 */

import { cornerstone } from './init.js';
import { getGPUInfo, getRecommendedBatchSize } from './gpuDetector.js';
import { planCacheBudget } from './cacheBudget.js';   // [10.0.7] потолок кэша — одним правилом
import { log } from '../utils/logger.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';

// [12.0.37] КАДРЫ ПОКАЗАННЫХ ТОМОВ — НЕ ВЫГРУЖАТЬ. Владелец: «запустил
// „Органы брюшной полости“, шёл подсчёт, затем всё перезагрузилось». Журнал:
// «JS Heap critical (100%)! Emergency eviction», и сразу у всех органов
// «e.extent is not iterable», окна MPR пустые. Аварийная выгрузка защищала
// только ±10 кадров вокруг текущего среза стопки. Но в Cornerstone 5 объёмный
// том не держит своей копии пикселей — он читает их из кадров кеша, и снятие
// кадров убивает показанный в MPR и 3D том. «Держать снимок» (snapshotGuard)
// сюда не доходило: оно защищало том по имени, а выгрузка шла по кадрам.
// Теперь кадры всех показанных и удерживаемых томов даёт viewport.js.
let _поставщикЗащиты = null;
/** viewport.js сообщает, чьи кадры трогать нельзя. */
export function задатьПоставщикаЗащищённыхКадров(функция) {
  _поставщикЗащиты = typeof функция === 'function' ? функция : null;
}

// ══════════════════════════════════════════════════════════════
// 1. TYPES & JSDOC
// ══════════════════════════════════════════════════════════════

/**
 * @typedef {Object} StreamingConfig
 * @property {number} maxCacheSizeMB
 * @property {number} warningThreshold
 * @property {number} evictionThreshold
 * @property {number} evictionTarget
 * @property {number} prefetchAhead
 * @property {number} prefetchBehind
 * @property {number} prefetchConcurrency
 * @property {boolean} firstImagePriority
 * @property {boolean} adaptiveBatchSize
 * @property {number} minBatchSize
 * @property {number} maxBatchSize
 * @property {number} monitorInterval
 */

/**
 * @callback ProgressCallback
 * @param {number} loaded
 * @param {number} total
 * @param {'first-image'|'background'|'complete'} phase
 */

// ══════════════════════════════════════════════════════════════
// 2. CONFIGURATION DEFAULTS
// ══════════════════════════════════════════════════════════════

const DEFAULT_CONFIG = Object.freeze({
  maxCacheSizeMB: 2048,
  warningThreshold: 0.75,
  evictionThreshold: 0.85,
  evictionTarget: 0.60,
  prefetchAhead: 10,
  prefetchBehind: 3,
  prefetchConcurrency: 3,
  firstImagePriority: true,
  adaptiveBatchSize: true,
  minBatchSize: 5,
  maxBatchSize: 30,
  monitorInterval: 5000,
});

const BYTES_IN_MB = 1048576;

// ══════════════════════════════════════════════════════════════
// 3. UTILITIES
// ══════════════════════════════════════════════════════════════

/**
 * Уступка главному потоку с минимальной задержкой.
 * scheduler.postTask (Chromium) — приоритет background.
 * setTimeout(0) — fallback для Firefox/Safari.
 */
function _yieldToUI() {
  // @ts-ignore — scheduler API пока не в TypeScript lib
  if (typeof scheduler !== 'undefined' && scheduler.postTask) {
    return new Promise(resolve => 
      scheduler.postTask(resolve, { priority: 'background' })
    );
  }
  return new Promise(resolve => setTimeout(resolve, 0));
}

// ══════════════════════════════════════════════════════════════
// 4. CORE ENGINE CLASS
// ══════════════════════════════════════════════════════════════

class StreamingEngine {
  constructor() {
    /** @type {StreamingConfig} */
    this._config = { ...DEFAULT_CONFIG };
    
    // State
    this._monitorTimer = null;
    this._prefetchQueue = [];
    this._backgroundAbort = null;
    this._prefetchAbort = null;
    this._currentImageIds = [];
    this._currentIndex = -1; // -1 = не установлен
    
    // Telemetry
    this._decodeTimings = [];
  }

  // ─── PUBLIC ACCESSOR ───────────────────────────────────────────────

  /**
   * Безопасное чтение конфигурации (копия).
   * Заменяет сломанный Proxy из v1.0.
   * @returns {StreamingConfig}
   */
  get config() {
    return { ...this._config };
  }

  // ─── INITIALIZATION ─────────────────────────────────────────────────

  /**
   * Инициализация менеджера с адаптацией под железо.
   * Адаптация НЕ перезаписывает явный пользовательский maxCacheSizeMB.
   * @param {Partial<StreamingConfig>} userConfig 
   */
  initialize(userConfig = {}) {
    // Сохраняем явный пользовательский лимит (если задан)
    const userMaxCache = userConfig.maxCacheSizeMB;

    this._config = { ...DEFAULT_CONFIG, ...userConfig };

    // Hardware Adaptation (только если пользователь НЕ задал лимит явно)
    const gpuInfo = getGPUInfo();
    if (gpuInfo && !userMaxCache) {
      // [10.0.7] Здесь стояло Math.max(1536, ...) для СЛАБЫХ устройств: телефону
      // гарантировался кэш не меньше полутора гигабайт вместо ограничения им.
      // Cornerstone послушно не чистил кэш, а телефонный браузер выгружал
      // вкладку задолго до 1.5 ГБ. Правило вынесено в cacheBudget.js.
      const budget = planCacheBudget({
        isMobile: !!gpuInfo.isMobile,
        tierLevel: gpuInfo.tier?.level ?? 2,
        vramMB: gpuInfo.vramEstimateMB || 512,
        deviceMemoryGB: (typeof navigator !== 'undefined' && navigator.deviceMemory) || 8,
      });
      this._config.maxCacheSizeMB = budget.mb;
      log.info(`[StreamingEngine] Потолок кэша: ${budget.mb} МБ (${budget.reason})`);
      if (gpuInfo.isMobile || gpuInfo.tier?.level === 0) {
        this._config.prefetchAhead = 5;
        this._config.prefetchConcurrency = 2;
      }
    } else if (userMaxCache) {
      log.debug(`[StreamingEngine] Using user-specified maxCacheSizeMB: ${userMaxCache}`);
    }

    this._applyCacheLimit();
    this._startMonitoring();

    log.info(`[StreamingEngine] Initialized: MaxCache=${this._config.maxCacheSizeMB}MB, Prefetch=${this._config.prefetchAhead}f/${this._config.prefetchBehind}b`);
  }

  // ─── PUBLIC PIPELINE ───────────────────────────────────────────────

  /**
   * Прогрессивная загрузка стека
   * @param {Object} viewport 
   * @param {string[]} imageIds 
   * @param {ProgressCallback} [onProgress] 
   */
  async loadStackProgressive(viewport, imageIds, onProgress) {
    this._resetState(imageIds);
    if (imageIds.length === 0) return;

    // Phase 1: High Priority First Image
    if (this._config.firstImagePriority) {
      try {
        const t0 = performance.now();
        await cornerstone.imageLoader.loadAndCacheImage(imageIds[0]);
        this._recordDecodeTiming(performance.now() - t0);
      } catch (e) {
        log.error('[StreamingEngine] First image load failed:', e);
      }
    }

    // Set stack immediately so user sees the first slice
    await viewport.setStack(imageIds);
    viewport.render();

    if (typeof onProgress === 'function') {
      onProgress(1, imageIds.length, 'first-image');
    }

    // Phase 2: Background Loading
    if (imageIds.length > 1) {
      this._backgroundAbort = new AbortController();
      this._executeBackgroundLoad(viewport, onProgress, this._backgroundAbort.signal).catch(e => {
        if (e.name !== 'AbortError') log.error('[StreamingEngine] Background load error:', e);
      });
    }
  }

  /**
   * Вызывать при скролле для обновления приоритетов префетча.
   * Игнорирует вызов если индекс не изменился.
   * @param {number} newIndex 
   * @param {string[]} [imageIds] 
   */
  onSliceChange(newIndex, imageIds) {
    if (imageIds) {
      this._currentImageIds = imageIds;
    }

    // Защита от двойного вызова с тем же индексом
    if (newIndex === this._currentIndex && !imageIds) return;

    this._currentIndex = newIndex;

    if (this._currentImageIds.length > 0) {
      this._schedulePrefetch();
    }
  }

  // ─── MEMORY MANAGEMENT (EVICTION) ──────────────────────────────────

  /**
   * Умная очистка кэша (LRU).
   * НИКОГДА не вызывает purgeCache() — это разрушает активный том.
   * @param {number} [targetRatio] 
   * @returns {number} Freed bytes
   */
  evictCache(targetRatio) {
    const target = targetRatio || this._config.evictionTarget;

    try {
      const cacheSize = cornerstone.cache.getCacheSize();
      const maxCache = cornerstone.cache.getMaxCacheSize();
      const currentRatio = cacheSize / maxCache;

      if (currentRatio <= target) return 0;

      const bytesToFree = cacheSize - (maxCache * target);
      log.info(`[StreamingEngine] Evicting ${(bytesToFree / BYTES_IN_MB).toFixed(1)}MB...`);

      const cachedImages = this._getSafelyCachedImageIds();
      const protectedSet = this._getProtectedImages();
      if (!protectedSet) return 0;   // [12.0.37] не знаем, что показано, — не трогаем

      let freed = 0;
      for (const imageId of cachedImages) {
        if (freed >= bytesToFree) break;
        if (protectedSet.has(imageId)) continue;

        const image = cornerstone.cache.getImageLoadObject(imageId);
        if (image) {
          const size = image.sizeInBytes || 0;
          try {
            cornerstone.cache.removeImageLoadObject(imageId);
            freed += size;
          } catch (rmErr) {
            // том/shared key — кадр пропускаем, том цел
          }
        }
      }

      log.info(`[StreamingEngine] Evicted ${(freed / BYTES_IN_MB).toFixed(1)}MB`);
      events.emit(EVENTS.CACHE_EVICTED, { freedMB: freed / BYTES_IN_MB });
      return freed;

    } catch (e) {
      log.warn('[StreamingEngine] Eviction skipped (cache untouched to protect active volume):', e?.message || e);
      return 0;
    }
  }

  // ─── PRIVATE METHODS ───────────────────────────────────────────────

  /** @private */
  _resetState(imageIds) {
    if (this._backgroundAbort) this._backgroundAbort.abort();
    if (this._prefetchAbort) this._prefetchAbort.abort();
    
    this._currentImageIds = imageIds;
    this._currentIndex = 0;
    this._prefetchQueue = [];
  }

  /** @private */
  async _executeBackgroundLoad(viewport, onProgress, signal) {
    const total = this._currentImageIds.length;
    let loaded = 1;

    for (let i = 1; i < total; i += this._getAdaptiveBatchSize()) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      const batchSize = this._getAdaptiveBatchSize();
      const batch = this._currentImageIds.slice(i, Math.min(i + batchSize, total));
      
      const t0 = performance.now();

      await Promise.allSettled(
        batch.map(id => cornerstone.imageLoader.loadAndCacheImage(id))
      );

      this._recordDecodeTiming((performance.now() - t0) / batch.length);
      loaded += batch.length;

      if (typeof onProgress === 'function') {
        onProgress(loaded, total, 'background');
      }

      this._checkMemory();

      // Уступка UI-потоку (минимальная задержка)
      await _yieldToUI();
    }

    if (typeof onProgress === 'function' && !signal.aborted) {
      onProgress(total, total, 'complete');
    }
  }

  /** @private */
  _schedulePrefetch() {
    if (this._prefetchAbort) this._prefetchAbort.abort();
    
    const total = this._currentImageIds.length;
    const queue = [];

    // Вперед (Высокий приоритет)
    for (let i = 1; i <= this._config.prefetchAhead; i++) {
      const idx = this._currentIndex + i;
      if (idx < total) queue.push({ id: this._currentImageIds[idx], priority: i });
    }

    // Назад (Низкий приоритет)
    for (let i = 1; i <= this._config.prefetchBehind; i++) {
      const idx = this._currentIndex - i;
      if (idx >= 0) queue.push({ id: this._currentImageIds[idx], priority: this._config.prefetchAhead + i });
    }

    this._prefetchQueue = queue.sort((a, b) => a.priority - b.priority).map(q => q.id);
    this._prefetchAbort = new AbortController();
    
    this._processPrefetchQueue(this._prefetchAbort.signal).catch(() => {});
  }

  /** @private */
  async _processPrefetchQueue(signal) {
    while (this._prefetchQueue.length > 0) {
      if (signal.aborted) return;

      const batch = this._prefetchQueue.splice(0, this._config.prefetchConcurrency);
      await Promise.allSettled(
        batch.map(id => cornerstone.imageLoader.loadAndCacheImage(id))
      );

      // Micro-pause
      await new Promise(r => setTimeout(r, 16));
    }
  }

  /**
   * Защищённый доступ к приватному API Cornerstone.
   * Полный try/catch с детальным логом — не падает при смене внутренностей CS3D.
   * @private
   * @returns {string[]}
   */
  _getSafelyCachedImageIds() {
    try {
      // Пытаемся через публичный API
      if (typeof cornerstone.cache.getImageIds === 'function') {
        const ids = cornerstone.cache.getImageIds();
        if (Array.isArray(ids)) return ids;
      }
    } catch (e) {
      log.debug('[StreamingEngine] Public API cache introspection failed:', e.message);
    }

    try {
      // Fallback: приватный _imageCache (может исчезнуть в будущих версиях)
      if (cornerstone.cache._imageCache instanceof Map) {
        return Array.from(cornerstone.cache._imageCache.keys());
      }
    } catch (e) {
      log.debug('[StreamingEngine] Private API cache introspection failed:', e.message);
    }

    try {
      // Последний fallback: перебор через getImageLoadObject
      const ids = [];
      if (typeof cornerstone.cache.getImageLoadObject === 'function') {
        // Не можем перебрать все ключи — возвращаем пустой массив
        log.debug('[StreamingEngine] No cache key enumeration available');
      }
      return ids;
    } catch (e) {
      log.warn('[StreamingEngine] All cache introspection methods failed:', e.message);
      return [];
    }
  }

  /** @private */
  _getProtectedImages() {
    const protectedSet = new Set();
    // [12.0.37] Кадры показанных и удерживаемых томов. Если список получить
    // не удалось — null: выгружать вслепую нельзя.
    if (_поставщикЗащиты) {
      try {
        for (const id of (_поставщикЗащиты() || [])) protectedSet.add(id);
      } catch (e) {
        log.warn('[StreamingEngine] список показанных томов недоступен — выгрузку пропускаю:', e?.message || e);
        return null;
      }
    }
    const radius = Math.max(this._config.prefetchAhead, 5);
    
    for (let i = -radius; i <= radius; i++) {
      const idx = this._currentIndex + i;
      if (idx >= 0 && idx < this._currentImageIds.length) {
        protectedSet.add(this._currentImageIds[idx]);
      }
    }
    return protectedSet;
  }

  // ─── TELEMETRY & ADAPTATION ────────────────────────────────────────

  /** @private */
  _recordDecodeTiming(ms) {
    this._decodeTimings.push(ms);
    if (this._decodeTimings.length > 50) this._decodeTimings.shift();
  }

  /** @private */
  _getAdaptiveBatchSize() {
    if (!this._config.adaptiveBatchSize || this._decodeTimings.length < 5) {
      return getRecommendedBatchSize() || 10;
    }

    const avgMs = this._decodeTimings.reduce((a, b) => a + b, 0) / this._decodeTimings.length;
    const targetBatchMs = 250;
    let computedSize = Math.round(targetBatchMs / Math.max(avgMs, 1));

    return Math.max(this._config.minBatchSize, Math.min(this._config.maxBatchSize, computedSize));
  }

  /** @private */
  _applyCacheLimit() {
    try {
      const bytes = this._config.maxCacheSizeMB * BYTES_IN_MB;
      if (typeof cornerstone.cache.setMaxCacheSize === 'function') {
        cornerstone.cache.setMaxCacheSize(bytes);
      }
    } catch (e) {
      log.error('[StreamingEngine] Failed to apply cache limit:', e);
    }
  }

  // ─── MONITORING ────────────────────────────────────────────────────

  /** @private */
  _startMonitoring() {
    this._stopMonitoring();
    this._monitorTimer = setInterval(() => this._checkMemory(), this._config.monitorInterval);
  }

  /** @private */
  _stopMonitoring() {
    if (this._monitorTimer) {
      clearInterval(this._monitorTimer);
      this._monitorTimer = null;
    }
  }

  /** @private */
  _checkMemory() {
    try {
      const cacheSize = cornerstone.cache.getCacheSize();
      const maxCache = cornerstone.cache.getMaxCacheSize();
      const ratio = cacheSize / maxCache;

      if (ratio >= this._config.evictionThreshold) {
        this.evictCache();
      } else if (ratio >= this._config.warningThreshold) {
        events.emit(EVENTS.CACHE_WARNING, { ratio, sizeMB: cacheSize / BYTES_IN_MB });
      }

      // Chrome/Edge Hardware memory monitoring (нестандартное расширение)
      if (performance && performance.memory) {
        const heapRatio = performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit;
        if (heapRatio > 0.90) {
          log.warn(`[StreamingEngine] JS Heap critical (${(heapRatio * 100).toFixed(0)}%)! Emergency eviction.`);
          this.evictCache(0.4);
        }
      }
    } catch (e) {
      log.warn('[StreamingEngine] Memory check failed:', e);
    }
  }

  // ─── DESTRUCTOR ────────────────────────────────────────────────────

  destroy() {
    this._stopMonitoring();
    if (this._backgroundAbort) this._backgroundAbort.abort();
    if (this._prefetchAbort) this._prefetchAbort.abort();
    this._currentImageIds = [];
    this._prefetchQueue = [];
    this._decodeTimings = [];
    this._currentIndex = -1;
    log.info('[StreamingEngine] Destroyed.');
  }

  // ─── GETTERS ───────────────────────────────────────────────────────

  getStats() {
    try {
      const cacheSize = cornerstone.cache.getCacheSize();
      const maxCache = cornerstone.cache.getMaxCacheSize();
      const avgDecode = this._decodeTimings.length > 0
        ? this._decodeTimings.reduce((a, b) => a + b, 0) / this._decodeTimings.length
        : 0;

      return {
        cacheMB: cacheSize / BYTES_IN_MB,
        maxMB: maxCache / BYTES_IN_MB,
        usageRatio: cacheSize / maxCache,
        avgDecodeMs: avgDecode,
        batchSize: this._getAdaptiveBatchSize(),
        prefetchQueue: this._prefetchQueue.length,
      };
    } catch (e) {
      return { error: e.message };
    }
  }
}

// ══════════════════════════════════════════════════════════════
// 5. EXPORT SINGLETON & API COMPATIBILITY LAYER
// ══════════════════════════════════════════════════════════════

export const streamingEngine = new StreamingEngine();

// Обратная совместимость (старые функции ссылаются на методы Singleton'a)
export const initStreamingManager = (config) => streamingEngine.initialize(config);
export const loadStackProgressive = (vp, ids, cb) => streamingEngine.loadStackProgressive(vp, ids, cb);
export const onSliceChange = (idx, ids) => streamingEngine.onSliceChange(idx, ids);
export const evictCache = (ratio) => streamingEngine.evictCache(ratio);
export const getStats = () => streamingEngine.getStats();
export const configure = (config) => streamingEngine.initialize(config);
export const destroy = () => streamingEngine.destroy();

// Экспорт конфига через геттер (без Proxy)
export { streamingEngine as _engine }; // для тестов